import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { endOfDay, formatDate, startOfDay } from 'date-fns';
import { useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import type { To } from 'react-router-dom';
import styled from 'styled-components';

import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BrandL,
  BrandXXS,
  HeadlineS,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { CardAvatar } from '@sorare/core/src/components/card/Avatar';
import { CloseButton } from '@sorare/core/src/components/dialog/CloseButton';
import { DateOrFixturePicker } from '@sorare/core/src/components/lobby/DateOrFixturePicker';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { range } from '@sorare/core/src/lib/arrays';
import { isType } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Game } from 'components/game/Game';

import type {
  TasksTrckStepGamePicker_decisivePlayerPickerTask,
  TasksTrckStepGamesPickerQuery,
  TasksTrckStepGamesPickerQueryVariables,
} from './__generated__/index.graphql';
import bg from './assets/bg.webp';

const PICKABLE_GAMES_QUERY = gql`
  query TasksTrckStepGamesPickerQuery(
    $id: ID!
    $after: String
    $fromDate: ISO8601DateTime
    $toDate: ISO8601DateTime
  ) {
    currentUser {
      slug
      task(id: $id) {
        id
        ... on DecisivePlayerPickerTask {
          id
          pickableGames(after: $after, fromDate: $fromDate, toDate: $toDate) {
            nodes {
              id
              game {
                id
                ...Game_anyGameInterface
              }
              cards {
                slug
                ...CardAvatar_anyCard
              }
              totalCount
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
    }
  }
  ${CardAvatar.fragments.anyCard}
  ${Game.fragments.anyGameInterface}
` as TypedDocumentNode<
  TasksTrckStepGamesPickerQuery,
  TasksTrckStepGamesPickerQueryVariables
>;

const StyledDrawer = styled(Drawer)`
  background: var(--c-black);

  @media ${tabletAndAbove} {
    min-width: 420px;
  }
`;

const DrawerContent = styled(Vertical)`
  background: url(${bg}) top center / 100% 200px no-repeat;
`;

const Root = styled(Vertical)`
  padding: 0 var(--double-unit) var(--double-unit);

  @media ${tabletAndAbove} {
    padding: calc(6 * var(--unit)) var(--double-unit) var(--double-unit)
      var(--double-unit);
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: var(--double-unit);
`;

const DateWrapper = styled(ButtonBase)`
  width: 55px;
  height: 55px;
  aspect-ratio: 1;
  border: 1px solid var(--c-nd-100);
  border-radius: var(--double-unit);
  padding: var(--unit) var(--intermediate-unit);

  &.selected {
    color: var(--c-black);
    background-color: var(--c-white);
  }
`;

const GameSkeleton = styled(Skeleton)`
  width: 100%;
  height: 80px;
  border-radius: var(--double-unit);
`;

const DateSkeleton = styled(Skeleton)`
  width: 55px;
  height: 55px;
  border-radius: var(--double-unit);
`;

const GameFooter = styled(Horizontal)`
  padding: var(--unit);
`;

const CardAvartarWrapper = styled.div`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: 50%;
`;

const CountWrapper = styled(CardAvartarWrapper)`
  border: 1px solid var(--c-nd-150-opaque);
  background: var(--c-nd-50);
  display: flex;
  align-items: center;
  justify-content: center;
`;

type Props = {
  decisivePlayerPickerTask: TasksTrckStepGamePicker_decisivePlayerPickerTask;
  initialDate?: string;
  to: (date: string, gameId: string) => To;
  open: boolean;
  onClose: () => void;
};

export const TasksTrckStepGamePicker = ({
  decisivePlayerPickerTask,
  initialDate,
  to,
  open,
  onClose,
}: Props) => {
  const isTablet = useIsTabletAndAbove();
  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    initialDate
  );

  const stepIdWithoutPrefix = idFromObject(decisivePlayerPickerTask.id);

  const dates = decisivePlayerPickerTask.pickableDays.nodes;

  const datesWithSlug = [
    ...dates.map(date => ({
      slug: formatDate(date.date, 'yyyy-MM-dd'),
      ...date,
    })),
  ];

  const reversedDatesWithSlug = [...datesWithSlug].reverse();

  const selectedDateObject = datesWithSlug.find(
    date => date.slug === selectedDate
  );

  const {
    data: gamesData,
    loadMore: loadMoreGames,
    loading: loadingGames,
  } = usePaginatedQuery(PICKABLE_GAMES_QUERY, {
    variables: {
      id: stepIdWithoutPrefix,
      fromDate: startOfDay(selectedDateObject?.date || new Date()),
      toDate: endOfDay(selectedDateObject?.date || new Date()),
    },
    connection: 'GameWithCardsPreviewConnection',
    skip: !selectedDateObject,
  });

  const pickableGames =
    gamesData?.currentUser?.task &&
    isType(gamesData?.currentUser?.task, 'DecisivePlayerPickerTask')
      ? gamesData?.currentUser?.task.pickableGames
      : undefined;

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMoreGames(false, {
        after: pickableGames?.pageInfo.endCursor || undefined,
      });
    },
    pickableGames?.pageInfo.hasNextPage || false,
    loadingGames
  );

  const games = pickableGames?.nodes;

  if (datesWithSlug.length > 0 && !selectedDate) {
    setSelectedDate(datesWithSlug.at(0)?.slug);
  }

  return (
    <StyledDrawer
      animated={false}
      open={open}
      onBackdropClick={onClose}
      onClose={onClose}
      fullScreen={!isTablet}
    >
      <DrawerContent>
        {!isTablet && (
          <Header>
            <CloseButton onClose={onClose} />
          </Header>
        )}
        <Root gap={3}>
          <BrandL>
            <FormattedMessage
              id="missions.TasksTrckStepGamePicker.title"
              defaultMessage="Select a game"
            />
          </BrandL>
          <DateOrFixturePicker
            items={reversedDatesWithSlug}
            value={
              datesWithSlug.find(date => date.slug === selectedDate) ||
              datesWithSlug[0]
            }
            renderItem={({ item: date }) => (
              <DateWrapper
                onClick={() => setSelectedDate(date.slug)}
                className={classNames({
                  selected: selectedDate === date.slug,
                })}
              >
                <Vertical gap={0} center>
                  <LabelS>
                    <FormattedDate value={date.date} weekday="short" />
                  </LabelS>
                  <HeadlineS>
                    <FormattedDate value={date.date} day="numeric" />
                  </HeadlineS>
                </Vertical>
              </DateWrapper>
            )}
            renderPlaceholder={() => <DateSkeleton />}
          />
          <Vertical gap={2}>
            {loadingGames && !games?.length ? (
              <>
                {range(6).map(i => (
                  <GameSkeleton key={i} />
                ))}
              </>
            ) : (
              (games || []).map(gameWithCardsPreview => {
                const { game } = gameWithCardsPreview;
                const { cards, totalCount } = gameWithCardsPreview;
                return (
                  <Game
                    game={game}
                    key={game.id}
                    to={to(
                      selectedDateObject
                        ? formatDate(selectedDateObject?.date, 'yyyy-MM-dd')
                        : '',
                      game.id
                    )}
                  >
                    {cards.length > 0 && (
                      <GameFooter center>
                        {(cards || []).map(card => (
                          <CardAvartarWrapper key={card.slug}>
                            <CardAvatar
                              key={card.slug}
                              className="rounded"
                              maxWidth={40}
                              card={card}
                            />
                          </CardAvartarWrapper>
                        ))}
                        {totalCount > 5 && (
                          <CountWrapper>
                            <BrandXXS>+{totalCount - 5}</BrandXXS>
                          </CountWrapper>
                        )}
                      </GameFooter>
                    )}
                  </Game>
                );
              })
            )}
            <InfiniteScrollLoader />
          </Vertical>
        </Root>
      </DrawerContent>
    </StyledDrawer>
  );
};

TasksTrckStepGamePicker.fragments = {
  decisivePlayerPickerTask: gql`
    fragment TasksTrckStepGamePicker_decisivePlayerPickerTask on DecisivePlayerPickerTask {
      id
      parentTask {
        id
      }
      pickableDays(timeZone: $timeZone) {
        nodes {
          id
          date
        }
      }
    }
  ` as TypedDocumentNode<TasksTrckStepGamePicker_decisivePlayerPickerTask>,
};
